import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Phone, MessageSquare, HelpCircle } from "lucide-react";
import { publicApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const GuidancePage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        company: "",
        application: "",
        message: "",
    });
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await publicApi.submitQuote({
                request_type: "guidance",
                product_id: null,
                product_name: null,
                ...formData
            });
            setSubmitted(true);
            toast.success("Guidance request submitted successfully!");
            navigate("/thank-you");
        } catch (err) {
            console.error("Failed to submit guidance request:", err);
            toast.error("Failed to submit request. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    
    if (submitted) {
        return (
            <div className="noise-overlay min-h-screen flex items-center justify-center">
                <div className="container mx-auto px-6 lg:px-12 max-w-2xl text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Check className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight mb-4" data-testid="guidance-success">
                        We'll Be in Touch
                    </h1>
                    <p className="text-muted-foreground mb-8">
                        Thank you for reaching out! One of our specialists will contact you within 24 hours to discuss your application and help you find the right equipment.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button 
                            onClick={() => navigate("/products")} 
                            variant="outline" 
                            className="rounded-none uppercase font-bold tracking-wider"
                        >
                            Browse Equipment
                        </Button>
                        <Button 
                            onClick={() => navigate("/compare")}
                            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider"
                        >
                            Compare Systems
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="noise-overlay min-h-screen">
            {/* Header */}
            <section className="py-12 lg:py-16 border-b border-border">
                <div className="container mx-auto px-6 lg:px-12">
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-2">Expert Support</p>
                    <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase">
                        Get Guidance
                    </h1>
                    <p className="text-muted-foreground mt-4 max-w-2xl">
                        Not sure which system fits your application? Talk to a Dry Ice Cleaning Specialist and get the right setup from day one.
                    </p>
                </div>
            </section>
            
            <div className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                {/* Value Props */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                    <Card className="bg-card border border-border rounded-none">
                        <CardContent className="p-6 flex items-start gap-4">
                            <div className="w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <MessageSquare className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                                <h3 className="font-heading font-bold mb-1">Free Consultation</h3>
                                <p className="text-muted-foreground text-sm">No-pressure conversation about your needs and options.</p>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-card border border-border rounded-none">
                        <CardContent className="p-6 flex items-start gap-4">
                            <div className="w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <HelpCircle className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                                <h3 className="font-heading font-bold mb-1">Unbiased Advice</h3>
                                <p className="text-muted-foreground text-sm">We recommend what's best for you, not a specific brand.</p>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-card border border-border rounded-none">
                        <CardContent className="p-6 flex items-start gap-4">
                            <div className="w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Phone className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                                <h3 className="font-heading font-bold mb-1">Quick Response</h3>
                                <p className="text-muted-foreground text-sm">We'll get back to you within 24 hours.</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Form */}
                    <div className="lg:col-span-2">
                        <Card className="bg-card border border-border rounded-none">
                            <CardContent className="p-6 lg:p-8">
                                <h2 className="font-heading font-bold text-xl mb-6">Tell Us About Your Application</h2>
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Name */}
                                        <div className="space-y-2">
                                            <Label htmlFor="name" className="font-mono text-xs uppercase tracking-wider">
                                                Full Name *
                                            </Label>
                                            <Input
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                                className="rounded-none bg-input border-transparent focus:border-primary h-12"
                                                placeholder="John Smith"
                                                data-testid="guidance-name"
                                            />
                                        </div>
                                        
                                        {/* Email */}
                                        <div className="space-y-2">
                                            <Label htmlFor="email" className="font-mono text-xs uppercase tracking-wider">
                                                Email Address *
                                            </Label>
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                                className="rounded-none bg-input border-transparent focus:border-primary h-12"
                                                placeholder="john@company.com"
                                                data-testid="guidance-email"
                                            />
                                        </div>
                                        
                                        {/* Phone */}
                                        <div className="space-y-2">
                                            <Label htmlFor="phone" className="font-mono text-xs uppercase tracking-wider">
                                                Phone Number
                                            </Label>
                                            <Input
                                                id="phone"
                                                name="phone"
                                                type="tel"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="rounded-none bg-input border-transparent focus:border-primary h-12"
                                                placeholder="+1 (555) 123-4567"
                                                data-testid="guidance-phone"
                                            />
                                        </div>
                                        
                                        {/* Company */}
                                        <div className="space-y-2">
                                            <Label htmlFor="company" className="font-mono text-xs uppercase tracking-wider">
                                                Company Name
                                            </Label>
                                            <Input
                                                id="company"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleInputChange}
                                                className="rounded-none bg-input border-transparent focus:border-primary h-12"
                                                placeholder="ACME Industries"
                                                data-testid="guidance-company"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Application */}
                                    <div className="space-y-2">
                                        <Label htmlFor="application" className="font-mono text-xs uppercase tracking-wider">
                                            What will you be cleaning? *
                                        </Label>
                                        <Input
                                            id="application"
                                            name="application"
                                            value={formData.application}
                                            onChange={handleInputChange}
                                            required
                                            className="rounded-none bg-input border-transparent focus:border-primary h-12"
                                            placeholder="e.g., Injection molds, automotive interiors, fire damage"
                                            data-testid="guidance-application"
                                        />
                                    </div>
                                    
                                    {/* Message */}
                                    <div className="space-y-2">
                                        <Label htmlFor="message" className="font-mono text-xs uppercase tracking-wider">
                                            Additional Context
                                        </Label>
                                        <Textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            rows={5}
                                            className="rounded-none bg-input border-transparent focus:border-primary resize-none"
                                            placeholder="Tell us about your current cleaning process, volume, frequency, and any challenges you're facing..."
                                            data-testid="guidance-message"
                                        />
                                    </div>
                                    
                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider py-6 text-lg"
                                        data-testid="guidance-submit-btn"
                                    >
                                        {loading ? "Submitting..." : "Request Guidance"}
                                        <ArrowRight className="ml-2 h-5 w-5" />
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    </div>
                    
                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Common Questions */}
                        <Card className="bg-secondary/50 border border-border rounded-none">
                            <CardContent className="p-6">
                                <h3 className="font-heading font-bold text-lg mb-4">Common Questions We Help With</h3>
                                <ul className="space-y-3">
                                    <li className="flex items-start gap-3 text-sm text-muted-foreground">
                                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        Which power class do I need?
                                    </li>
                                    <li className="flex items-start gap-3 text-sm text-muted-foreground">
                                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        Do I need abrasive capability?
                                    </li>
                                    <li className="flex items-start gap-3 text-sm text-muted-foreground">
                                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        Mobile vs. stationary setup?
                                    </li>
                                    <li className="flex items-start gap-3 text-sm text-muted-foreground">
                                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        Air compressor requirements
                                    </li>
                                    <li className="flex items-start gap-3 text-sm text-muted-foreground">
                                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        ROI and operational costs
                                    </li>
                                </ul>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GuidancePage;
